import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const defaultWeekStart = () => {
  const date = new Date();
  const day = date.getDay();
  date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
  return date.toISOString().slice(0, 10);
};

const starlineTimeOptions = Array.from({ length: 14 }, (_, index) => {
  const hour24 = index + 10;
  const hour12 = hour24 > 12 ? hour24 - 12 : hour24;
  const suffix = hour24 >= 12 ? "PM" : "AM";
  return `${String(hour12).padStart(2, "0")}:00 ${suffix}`;
});

function AdminCharts() {
  const [markets, setMarkets] = useState([]);
  const [starlineMarkets, setStarlineMarkets] = useState([]);
  const [rows, setRows] = useState([]);
  const [chartFiles, setChartFiles] = useState([]);
  const [importBatches, setImportBatches] = useState([]);
  const [form, setForm] = useState({ market_id: "", chart_date: new Date().toISOString().slice(0, 10), open_pana: "", open_digit: "", close_pana: "", close_digit: "" });
  const [starlineForm, setStarlineForm] = useState({ market_id: "", chart_date: new Date().toISOString().slice(0, 10), result_hour: "10:00 AM", pana: "", digit: "" });
  const [deleteForm, setDeleteForm] = useState({ market_id: "", chart_date: new Date().toISOString().slice(0, 10) });
  const [fileForm, setFileForm] = useState({ market_id: "", chart_type: "jodi", latest_week_start: defaultWeekStart(), work_days_pattern: "mon_sat", file: null });
  const [settings, setSettings] = useState({ chart_auto_update_enabled: "1", starline_chart_auto_update_enabled: "1" });
  const [fileImportSummary, setFileImportSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    API.get("/markets").then((res) => {
      const allMarkets = Array.isArray(res.data) ? res.data : [];
      const normal = allMarkets.filter((market) => market.market_type !== "starline");
      const starline = allMarkets.filter((market) => market.market_type === "starline");
      setMarkets(normal);
      setStarlineMarkets(starline);
      setForm((current) => ({ ...current, market_id: current.market_id || normal[0]?.id || "" }));
      setStarlineForm((current) => ({ ...current, market_id: current.market_id || starline[0]?.id || "" }));
      setDeleteForm((current) => ({ ...current, market_id: current.market_id || normal[0]?.id || "" }));
      setFileForm((current) => ({
        ...current,
        market_id: current.market_id || (current.chart_type === "starline" ? starline[0]?.id : normal[0]?.id) || ""
      }));
    });
    API.get("/charts").then((res) => setRows(Array.isArray(res.data) ? res.data : [])).catch(() => setRows([]));
    API.get("/admin/charts/files").then((res) => setChartFiles(Array.isArray(res.data) ? res.data : [])).catch(() => setChartFiles([]));
    API.get("/admin/charts/import-batches").then((res) => setImportBatches(Array.isArray(res.data) ? res.data : [])).catch(() => setImportBatches([]));
    API.get("/admin/charts/settings").then((res) => setSettings(res.data || { chart_auto_update_enabled: "1", starline_chart_auto_update_enabled: "1" })).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async (event) => {
    event.preventDefault();
    await API.post("/admin/charts", form);
    setForm((current) => ({ ...current, open_pana: "", open_digit: "", close_pana: "", close_digit: "" }));
    load();
  };

  const saveStarline = async (event) => {
    event.preventDefault();
    await API.post("/admin/charts/starline", starlineForm);
    setStarlineForm((current) => ({ ...current, pana: "", digit: "" }));
    alert("Starline chart entry saved");
    load();
  };

  const removeByDate = async (event) => {
    event.preventDefault();
    if (!deleteForm.market_id || !deleteForm.chart_date) {
      alert("Select market and chart date");
      return;
    }
    if (!window.confirm("Delete chart entry for this market and date?")) return;
    try {
      const res = await API.delete("/admin/charts/by-date", { params: deleteForm });
      alert(`Deleted ${Number(res.data?.deleted || 0)} chart row`);
      load();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete chart by date");
    }
  };

  const remove = async (id) => {
    await API.delete(`/admin/charts/${id}`);
    load();
  };

  const removeChartFile = async (id) => {
    if (!window.confirm("Delete this uploaded chart file record?")) return;
    await API.delete(`/admin/charts/files/${id}`);
    load();
  };

  const removeImportBatch = async (id) => {
    if (!window.confirm("Delete this imported chart batch and all rows added by it?")) return;
    await API.delete(`/admin/charts/import-batches/${id}`);
    load();
  };

  const clearAllCharts = async () => {
    if (!window.confirm("Delete all market chart rows, imported batches and uploaded chart files?")) return;
    setBusy(true);
    try {
      await API.post("/admin/charts/clear-all");
      setFileImportSummary(null);
      load();
      alert("All charts cleared");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to clear charts");
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await API.post("/admin/charts/settings", settings);
      alert("Chart settings saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save chart settings");
    } finally {
      setBusy(false);
    }
  };

  const importChartFile = async (event, overrideChartType = "") => {
    event.preventDefault();
    if (!fileForm.file) {
      alert("Please select a chart HTML or PDF file first");
      return;
    }

    setBusy(true);
    try {
      const effectiveChartType = overrideChartType || fileForm.chart_type;
      const data = new FormData();
      data.append("market_id", fileForm.market_id);
      data.append("chart_type", effectiveChartType);
      data.append("latest_week_start", fileForm.latest_week_start);
      data.append("work_days_pattern", fileForm.work_days_pattern);
      data.append("chart_file", fileForm.file);

      const res = await API.post("/admin/charts/import-file", data);
      setFileImportSummary(res.data || null);
      setFileForm((current) => ({ ...current, file: null }));
      const fileInput = document.getElementById("chartFileUploadInput");
      if (fileInput) fileInput.value = "";
      alert("Chart file imported");
      load();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to import chart file");
    } finally {
      setBusy(false);
    }
  };

  const fileMarkets = fileForm.chart_type === "starline" ? starlineMarkets : markets;
  const isStarlineImport = fileForm.chart_type === "starline";

  const uploadChartFileForm = (
    <form style={uploadCard} onSubmit={importChartFile}>
      <div>
        <h3 style={sectionTitle}>Upload Chart File</h3>
        <p style={sectionSub}>Upload HTML or PDF chart files here. Starline HTML import will fill all time rows from the file.</p>
      </div>
      <div style={fileGrid}>
        <label style={toggleBox}>
          <span>{fileForm.chart_type === "starline" ? "Starline Market" : "Market"}</span>
          <select style={input} value={fileForm.market_id} onChange={(event) => setFileForm((current) => ({ ...current, market_id: event.target.value }))}>
            {fileMarkets.map((market) => <option key={market.id} value={market.id}>{market.name}</option>)}
          </select>
        </label>
        <label style={toggleBox}>
          <span>Chart Type</span>
          <select style={input} value={fileForm.chart_type} onChange={(event) => setFileForm((current) => ({ ...current, chart_type: event.target.value, market_id: event.target.value === "starline" ? (starlineMarkets[0]?.id || "") : (markets[0]?.id || "") }))}>
            <option value="jodi">Jodi Chart</option>
            <option value="panel">Panel Chart</option>
            <option value="starline">Starline Chart</option>
          </select>
        </label>
        <label style={toggleBox}>
          <span>{fileForm.chart_type === "starline" ? "Fallback Year Date" : "Latest Row Week Starts"}</span>
          <input
            style={input}
            type="date"
            value={fileForm.latest_week_start}
            onChange={(event) => setFileForm((current) => ({ ...current, latest_week_start: event.target.value }))}
          />
        </label>
        {fileForm.chart_type !== "starline" && (
          <label style={toggleBox}>
            <span>{fileForm.chart_type === "panel" ? "Panel Work Days" : "Market Work Days"}</span>
            <select
              style={input}
              value={fileForm.work_days_pattern}
              onChange={(event) => setFileForm((current) => ({ ...current, work_days_pattern: event.target.value }))}
            >
              <option value="mon_sat">Monday to Saturday</option>
              <option value="mon_fri">Monday to Friday</option>
              <option value="sun_fri">Sunday to Friday</option>
              <option value="mon_sun">Monday to Sunday</option>
            </select>
          </label>
        )}
        <label style={toggleBox}>
          <span>{fileForm.chart_type === "starline" ? "HTML File" : "HTML / PDF File"}</span>
          <input
            id="chartFileUploadInput"
            style={fileInput}
            type="file"
            accept={fileForm.chart_type === "starline" ? ".html,.htm,text/html" : ".html,.htm,.pdf,text/html,application/pdf"}
            onChange={(event) => setFileForm((current) => ({ ...current, file: event.target.files?.[0] || null }))}
          />
        </label>
      </div>
      <div style={actionRow}>
        <button type="submit" style={uploadButton} disabled={busy}>Upload Chart File</button>
        <button type="button" style={dangerButton} disabled={busy} onClick={clearAllCharts}>Delete All Charts</button>
      </div>
      {fileImportSummary && (
        <div style={summaryCard}>
          <strong>{fileImportSummary.market} {String(fileImportSummary.chart_type || "").toUpperCase()} imported</strong>
          {fileImportSummary.mode === "pdf_import" ? <div>Source: {fileImportSummary.original_name || "PDF chart file"}</div> : null}
          <div>Parsed rows: {fileImportSummary.parsed_rows || 0}</div>
          <div>Saved rows: {fileImportSummary.imported_rows || 0}</div>
        </div>
      )}
    </form>
  );

  const starlineUploadForm = (
    <form
      style={starlineUploadCard}
      onSubmit={(event) => {
        event.preventDefault();
        importChartFile(event, "starline");
      }}
    >
      <div>
        <h3 style={sectionTitle}>Starline Old Chart Upload</h3>
        <p style={sectionSub}>Upload old Starline HTML chart file and fill hourly rows from 10:00 AM to 11:00 PM.</p>
      </div>
      <div style={fileGrid}>
        <label style={toggleBox}>
          <span>Starline Market</span>
          <select
            style={input}
            value={isStarlineImport ? fileForm.market_id : (starlineMarkets[0]?.id || "")}
            onChange={(event) => setFileForm((current) => ({ ...current, chart_type: "starline", market_id: event.target.value }))}
          >
            {starlineMarkets.map((market) => <option key={market.id} value={market.id}>{market.name}</option>)}
          </select>
        </label>
        <label style={toggleBox}>
          <span>Fallback Year Date</span>
          <input
            style={input}
            type="date"
            value={fileForm.latest_week_start}
            onChange={(event) => setFileForm((current) => ({ ...current, chart_type: "starline", latest_week_start: event.target.value }))}
          />
        </label>
        <label style={toggleBox}>
          <span>HTML File</span>
          <input
            id="starlineChartFileUploadInput"
            style={fileInput}
            type="file"
            accept=".html,.htm,text/html"
            onChange={(event) => setFileForm((current) => ({ ...current, chart_type: "starline", file: event.target.files?.[0] || null }))}
          />
        </label>
      </div>
      <div style={actionRow}>
        <button type="submit" style={starlineButton} disabled={busy || !starlineMarkets.length}>Upload Starline HTML</button>
      </div>
    </form>
  );

  return (
    <AdminLayout>
      <div style={page}>
        <div style={head}>
          <div>
            <h1 style={title}>Matka Charts</h1>
            <p style={sub}>Auto updates from declared results, with manual edit option</p>
          </div>
        </div>
        {uploadChartFileForm}
        {starlineUploadForm}
        <form style={card} onSubmit={save}>
          <div style={sectionHeading}>Manual Chart Entry</div>
          <select style={input} value={form.market_id} onChange={(event) => setForm((c) => ({ ...c, market_id: event.target.value }))}>
            {markets.map((market) => <option key={market.id} value={market.id}>{market.name}</option>)}
          </select>
          <input style={input} type="date" value={form.chart_date} onChange={(event) => setForm((c) => ({ ...c, chart_date: event.target.value }))} />
          <input style={input} placeholder="Open pana" value={form.open_pana} onChange={(event) => setForm((c) => ({ ...c, open_pana: event.target.value.replace(/\D/g, "").slice(0, 3) }))} />
          <input style={input} placeholder="Open digit" value={form.open_digit} onChange={(event) => setForm((c) => ({ ...c, open_digit: event.target.value.replace(/\D/g, "").slice(0, 1) }))} />
          <input style={input} placeholder="Close pana" value={form.close_pana} onChange={(event) => setForm((c) => ({ ...c, close_pana: event.target.value.replace(/\D/g, "").slice(0, 3) }))} />
          <input style={input} placeholder="Close digit" value={form.close_digit} onChange={(event) => setForm((c) => ({ ...c, close_digit: event.target.value.replace(/\D/g, "").slice(0, 1) }))} />
          <button style={button}>Save Chart</button>
        </form>
        <form style={starlineCard} onSubmit={saveStarline}>
          <div style={sectionHeading}>Starline Chart Entry</div>
          <select style={input} value={starlineForm.market_id} onChange={(event) => setStarlineForm((current) => ({ ...current, market_id: event.target.value }))}>
            {starlineMarkets.map((market) => <option key={market.id} value={market.id}>{market.name}</option>)}
          </select>
          <input style={input} type="date" value={starlineForm.chart_date} onChange={(event) => setStarlineForm((current) => ({ ...current, chart_date: event.target.value }))} />
          <select style={input} value={starlineForm.result_hour} onChange={(event) => setStarlineForm((current) => ({ ...current, result_hour: event.target.value }))}>
            {starlineTimeOptions.map((time) => <option key={time} value={time}>{time}</option>)}
          </select>
          <input style={input} placeholder="Panna" value={starlineForm.pana} onChange={(event) => setStarlineForm((current) => ({ ...current, pana: event.target.value.replace(/\D/g, "").slice(0, 3) }))} />
          <input style={input} placeholder="Digit" value={starlineForm.digit} onChange={(event) => setStarlineForm((current) => ({ ...current, digit: event.target.value.replace(/\D/g, "").slice(0, 1) }))} />
          <button style={starlineButton} disabled={!starlineMarkets.length}>Save Starline</button>
        </form>
        <form style={deleteCard} onSubmit={removeByDate}>
          <div>
            <h3 style={sectionTitle}>Delete Chart Entry Date Wise</h3>
            <p style={sectionSub}>Remove old Jodi/Panel row for one market on one date.</p>
          </div>
          <div style={deleteGrid}>
            <select style={input} value={deleteForm.market_id} onChange={(event) => setDeleteForm((current) => ({ ...current, market_id: event.target.value }))}>
              {markets.map((market) => <option key={market.id} value={market.id}>{market.name}</option>)}
            </select>
            <input style={input} type="date" value={deleteForm.chart_date} onChange={(event) => setDeleteForm((current) => ({ ...current, chart_date: event.target.value }))} />
            <button type="submit" style={dangerButton}>Delete By Date</button>
          </div>
        </form>
        <section style={settingsCard}>
          <div>
            <h3 style={sectionTitle}>Chart Auto Update</h3>
            <p style={sectionSub}>Declared results se automatic chart rows on or off rakho. External legacy import removed here.</p>
          </div>
          <div style={settingsGrid}>
            <label style={toggleBox}>
              <span>Normal Market Charts</span>
              <select style={input} value={settings.chart_auto_update_enabled} onChange={(event) => setSettings((current) => ({ ...current, chart_auto_update_enabled: event.target.value }))}>
                <option value="1">On</option>
                <option value="0">Off</option>
              </select>
            </label>
            <label style={toggleBox}>
              <span>Starline Charts</span>
              <select style={input} value={settings.starline_chart_auto_update_enabled} onChange={(event) => setSettings((current) => ({ ...current, starline_chart_auto_update_enabled: event.target.value }))}>
                <option value="1">On</option>
                <option value="0">Off</option>
              </select>
            </label>
          </div>
          <div style={actionRow}>
            <button type="button" style={button} onClick={saveSettings} disabled={busy}>Save Chart Settings</button>
          </div>
        </section>
        <section style={tableCard}>
          <div style={tableHead}>Imported HTML / PDF Chart Batches</div>
          {!importBatches.length ? <div style={emptyLine}>No imported chart batches yet.</div> : null}
          {importBatches.map((batch) => (
            <div key={batch.id} style={fileRow}>
              <div style={fileMeta}>
                <strong>{batch.market_name}</strong>
                <span>{String(batch.chart_type || "").toUpperCase()} · {String(batch.source_type || "").toUpperCase()}</span>
                <span>{batch.original_name || "-"}</span>
                <span>{Number(batch.imported_rows || 0)} rows · {formatWorkDays(batch.work_days_pattern)}</span>
              </div>
              <button type="button" style={deleteButton} onClick={() => removeImportBatch(batch.id)}>Delete Import</button>
            </div>
          ))}
        </section>
        <section style={tableCard}>
          <div style={tableHead}>Uploaded Chart Files</div>
          {!chartFiles.length ? <div style={emptyLine}>No legacy uploaded files saved.</div> : null}
          {chartFiles.map((file) => (
            <div key={file.id} style={fileRow}>
              <div style={fileMeta}>
                <strong>{file.market_name}</strong>
                <span>{String(file.chart_type || "").toUpperCase()}</span>
                <span>{file.original_name || file.file_url || "-"}</span>
              </div>
              <button type="button" style={deleteButton} onClick={() => removeChartFile(file.id)}>Delete File</button>
            </div>
          ))}
        </section>
        <section style={tableCard}>
          <div style={tableHead}>Chart Entries</div>
          {rows.slice(0, 120).map((row) => (
            <div key={row.id} style={tr}>
              <strong>{row.market_name}</strong>
              <span>{new Date(row.chart_date).toLocaleDateString("en-IN")}</span>
              <span>{row.open_pana || "***"}-{row.open_digit || "*"}</span>
              <span>{row.close_pana || "***"}-{row.close_digit || "*"}</span>
              <button type="button" style={deleteButton} onClick={() => remove(row.id)}>Delete</button>
            </div>
          ))}
        </section>
      </div>
    </AdminLayout>
  );
}

export default AdminCharts;

function formatWorkDays(value = "") {
  if (value === "mon_fri") return "Mon-Fri";
  if (value === "sun_fri") return "Sun-Fri";
  if (value === "mon_sun") return "Mon-Sun";
  return "Mon-Sat";
}

const page = { padding: 24, display: "grid", gap: 18 };
const head = { display: "flex", justifyContent: "space-between", marginBottom: 18 };
const title = { margin: 0, fontSize: 26, fontWeight: 900 };
const sub = { margin: "6px 0 0", color: "#64748b" };
const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 };
const starlineCard = { ...card, border: "1px solid #bfdbfe", background: "linear-gradient(135deg,#eff6ff,#ffffff)", boxShadow: "0 14px 34px rgba(37,99,235,.08)" };
const sectionHeading = { gridColumn: "1 / -1", fontWeight: 900, color: "#0f172a", fontSize: 16, marginBottom: 2 };
const deleteCard = { background: "#fff", border: "1px solid #fecaca", borderRadius: 14, padding: 16, display: "grid", gap: 14 };
const deleteGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 };
const uploadCard = { marginBottom: 18, background: "linear-gradient(135deg, #f0fdf4, #ecfeff)", border: "1px solid #99f6e4", borderRadius: 16, padding: 16, display: "grid", gap: 14, boxShadow: "0 18px 40px rgba(15, 118, 110, 0.10)" };
const starlineUploadCard = { marginBottom: 18, background: "linear-gradient(135deg, #eff6ff, #f5f3ff)", border: "1px solid #bfdbfe", borderRadius: 16, padding: 16, display: "grid", gap: 14, boxShadow: "0 18px 40px rgba(37, 99, 235, 0.12)" };
const settingsCard = { marginTop: 18, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 16, display: "grid", gap: 14 };
const sectionTitle = { margin: 0, fontSize: 18, fontWeight: 900 };
const sectionSub = { margin: "6px 0 0", color: "#64748b", fontSize: 13 };
const settingsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 };
const fileGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 };
const toggleBox = { display: "grid", gap: 8, fontWeight: 700, color: "#0f172a" };
const input = { height: 44, border: "1px solid #cbd5e1", padding: "0 12px", borderRadius: 8, fontWeight: 700 };
const fileInput = { ...input, padding: "9px 12px", background: "#f8fafc" };
const button = { border: 0, borderRadius: 8, background: "#2563eb", color: "#fff", fontWeight: 800 };
const starlineButton = { ...button, minHeight: 44, background: "linear-gradient(135deg,#2563eb,#7c3aed)" };
const uploadButton = { ...button, background: "linear-gradient(135deg, #16a34a, #0f766e)", minHeight: 44, padding: "0 18px" };
const dangerButton = { ...button, background: "linear-gradient(135deg, #dc2626, #991b1b)", minHeight: 44, padding: "0 18px" };
const actionRow = { display: "flex", gap: 10, flexWrap: "wrap" };
const summaryCard = { padding: 14, borderRadius: 12, background: "#eff6ff", border: "1px solid #bfdbfe", display: "grid", gap: 6, color: "#1e3a8a", fontSize: 13 };
const tableCard = { marginTop: 18, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" };
const tableHead = { padding: 14, fontWeight: 900, borderBottom: "1px solid #e5e7eb" };
const tr = { display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 100px", gap: 12, alignItems: "center", padding: 14, borderBottom: "1px solid #f1f5f9" };
const fileRow = { display: "grid", gridTemplateColumns: "1fr 110px", gap: 12, alignItems: "center", padding: 14, borderBottom: "1px solid #f1f5f9" };
const fileMeta = { display: "grid", gap: 4, color: "#334155", fontSize: 13 };
const emptyLine = { padding: 14, color: "#64748b", fontWeight: 600 };
const deleteButton = { height: 38, border: 0, borderRadius: 8, background: "#ef4444", color: "#fff", fontWeight: 800 };
